import React, { useEffect, useRef, useState, useId, useMemo } from 'react';
import {
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CButton, CAlert, CSpinner,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilCamera, cilX } from '@coreui/icons';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

const MAX_SERIAL = 50;
const ELEMENT_WAIT_MS = 4000;
// Extra delay after the modal opens so its CSS transition finishes and the
// scanner div has non-zero dimensions before html5-qrcode measures it.
const MODAL_OPEN_DELAY_MS = 300;

/** Symbologies passed to html5-qrcode `formatsToSupport` (QR + common product / serial barcodes). */
const SCAN_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.PDF_417,
];

function waitForDomElement(elementId, shouldAbort) {
  return new Promise((resolve, reject) => {
    const start = performance.now();
    let rafId = 0;
    const step = () => {
      if (shouldAbort()) {
        cancelAnimationFrame(rafId);
        reject(new Error('aborted'));
        return;
      }
      const el = document.getElementById(elementId);
      // Wait until the element exists AND has non-zero width (modal fully open).
      if (el && el.offsetWidth > 0) {
        resolve(el);
        return;
      }
      if (performance.now() - start > ELEMENT_WAIT_MS) {
        reject(new Error('Scanner area not ready. Try again.'));
        return;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
  });
}

/**
 * Opens device camera, reads QR or supported barcodes, passes trimmed text to onDecoded (max 50 chars), then closes.
 */
export default function SerialQrScanModal({ visible, onClose, onDecoded }) {
  const reactId = useId();
  const readerId = useMemo(() => `serial-qr-${reactId.replace(/:/g, '')}`, [reactId]);
  const scannerRef = useRef(null);
  const onDecodedRef = useRef(onDecoded);
  const onCloseRef = useRef(onClose);
  onDecodedRef.current = onDecoded;
  onCloseRef.current = onClose;

  const [camError, setCamError] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!visible) {
      if (scannerRef.current) {
        const s = scannerRef.current;
        scannerRef.current = null;
        s.stop()
          .then(() => s.clear())
          .catch(() => {});
      }
      setCamError('');
      setStarting(false);
      return undefined;
    }

    let cancelled = false;
    const shouldAbort = () => cancelled;

    setCamError('');
    setStarting(true);

    new Promise(r => setTimeout(r, MODAL_OPEN_DELAY_MS))
      .then(() => waitForDomElement(readerId, shouldAbort))
      .then(() => {
        if (cancelled) return;
        let qr;
        try {
          qr = new Html5Qrcode(readerId, false);
        } catch (e) {
          const msg = typeof e === 'string' ? e : (e?.message || String(e));
          throw new Error(msg || 'Could not create scanner.');
        }
        scannerRef.current = qr;
        const config = {
          fps: 10,
          /* Wider box helps 1D barcodes (CODE_128, EAN, etc.); QR still works inside it. */
          qrbox: { width: 300, height: 160 },
          formatsToSupport: SCAN_FORMATS,
        };

        const onSuccess = (decodedText) => {
          if (cancelled) return;
          const t = String(decodedText || '').trim().slice(0, MAX_SERIAL);
          onDecodedRef.current(t);
          const inst = scannerRef.current;
          if (!inst) { onCloseRef.current(); return; }
          inst.stop()
            .then(() => inst.clear())
            .catch(() => {})
            .finally(() => { scannerRef.current = null; onCloseRef.current(); });
        };

        // Prefer rear camera on mobile; fall back to any camera (desktop webcam).
        return qr.start({ facingMode: 'environment' }, config, onSuccess, () => {})
          .catch(() => {
            if (cancelled) return;
            return qr.start({ facingMode: 'user' }, config, onSuccess, () => {});
          })
          .catch(() => {
            if (cancelled) return;
            // Last resort: let the browser pick any available camera.
            return qr.start({ facingMode: { ideal: 'environment' } }, config, onSuccess, () => {});
          });
      })
      .then(() => {
        if (!cancelled) setStarting(false);
      })
      .catch((err) => {
        if (cancelled || err?.message === 'aborted') return;
        const msg = typeof err === 'string' ? err : (err?.message || 'Could not start camera.');
        setCamError(msg || 'Allow camera access or type the serial manually.');
        setStarting(false);
        scannerRef.current = null;
      });

    return () => {
      cancelled = true;
      if (scannerRef.current) {
        const s = scannerRef.current;
        scannerRef.current = null;
        s.stop().then(() => s.clear()).catch(() => {});
      }
    };
  }, [visible, readerId]);

  return (
    <CModal
      visible={visible}
      onClose={onClose}
      alignment="center"
      size="lg"
      transition={false}
    >
      <CModalHeader>
        <CModalTitle>
          <CIcon icon={cilCamera} className="me-2 text-success" />
          Scan serial (QR / barcode)
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p className="text-muted small mb-2">
          Point the camera at a <strong>QR code</strong> or <strong>barcode</strong> on the label (e.g. CODE 128, EAN-13). The decoded value fills <strong>Serial Number (No. Siri)</strong>.
        </p>
        {camError && <CAlert color="warning" className="py-2">{camError}</CAlert>}
        {starting && !camError && (
          <div className="text-center py-4">
            <CSpinner color="success" className="me-2" /> Starting camera…
          </div>
        )}
        <div id={readerId} className="rounded overflow-hidden bg-dark" style={{ minHeight: 280 }} />
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>
          <CIcon icon={cilX} className="me-1" /> Cancel
        </CButton>
      </CModalFooter>
    </CModal>
  );
}
